import _ from "lodash";
import diff from "microdiff";
import { CAYUSE_TYPES, FIELD_TYPES } from "globals/constants";

import { formatConflictMessage } from "./formatConflictMessage";

export const isUploadFieldType = field => (field?.cayuseType ?? field?.type) === FIELD_TYPES.UPLOAD;
export const isResearchTeamFieldType = field => field?.cayuseType === CAYUSE_TYPES.RESEARCH_TEAM;

const removeNullOrUndefined = obj => {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }

    if(obj instanceof Date) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(removeNullOrUndefined).filter(val => ![null, undefined, '', NaN].includes(val));
    }

    const newObjEntries = Object.entries(obj)
        .map(([key, val]) => [key, removeNullOrUndefined(val)])
        .filter(([_, val]) => ![null, undefined, '', NaN].includes(val));

    return Object.fromEntries(newObjEntries);
};

const mergeItem = (item, object) => {
    switch (item.type) {
        case "CREATE":
        case "CHANGE": {
            _.set(object, item.path, item.value);
            break;
        }
        case "REMOVE": {
            _.unset(object, item.path);
            break;
        }
    }

    return object;
};

const getField = (conflict, formElements) => {
    let field = formElements.find((item) => conflict.path[0] === item.value || conflict.path[1] === item.value);

    if (!field) {
        /*
            Get key of field from 'value'.
            Case: when field is created (added to the root object) and key does not exist in path
         */
        if (_.isPlainObject(conflict.value)) {
            const key = Object.keys(conflict.value)[0];
            field = formElements.find((item) => key.includes(item.value));
        } else {
            const fullPath = conflict.path.reduce((acc, v) => {
                acc += v;
                return acc;
            }, "");

            field = formElements.find((item) => fullPath.includes(item.value));
        }
    }

    return field;
};

/**
 * @param {{}} source - initial form variables
 * @param {{}} changes - user payload
 * @param {{}} conflictPayload - last saved form variables
 * @param {string} payloadContentType
 * @param {{ isModification?: boolean }}
 * @returns {payload: any}
 */
 export const mergePayload = (
    source,
    changes,
    conflictPayload,
    payloadContentType,
    {
        isModification,
    } = { isModification: false },
) => {
    let conflictsDiff;
    let changesDiff;

    if(isModification) {
        // difference between source data and conflict data
        conflictsDiff = diff(source?.awardContent ?? {}, conflictPayload?.awardContent ?? {});
        // difference between changes and conflict data
        changesDiff = diff(changes?.awardContent ?? {}, conflictPayload?.awardContent ?? {});

    } else {
        // difference between source data and conflict data
        conflictsDiff = diff(source?.root[payloadContentType] ?? {}, conflictPayload?.root[payloadContentType] ?? {});
        // difference between changes and conflict data
        changesDiff = diff(changes?.root[payloadContentType] ?? {}, conflictPayload?.root[payloadContentType] ?? {});
    }

    let payload = _.cloneDeep(changes);

    for (const conflict of conflictsDiff) {
        const hasValue = changesDiff.find((item) =>
            _.isEqual(item.path, conflict.path)
        );

        if(hasValue) {
            if(isModification) {
                conflict.path.unshift('awardContent');
            } else {
                conflict.path = ['root', payloadContentType, ...conflict.path];
            }
            payload = mergeItem(conflict, payload);
        }
    }

    return payload;
};

/**
 * @param {{ path: string, field: Record<string, any>, conflict: boolean, value: any, oldValue: any|undefined }[]} conflicts
 * @returns {{ sectionName: string, fieldName: string, value: any, oldValue: any }[]}
 */
export const formatConflictingFields = (conflicts) => {
    const groupMap = conflicts.reduce(
        (acc, conflict) => {
            const field = conflict.field;
            const type = field.cayuseType ?? field.type;

            if (isUploadFieldType(field)) {
                const key = field.value;
                acc.set(key, { field, group: [...(acc.get(key)?.group ?? []), { ...conflict, field }] });
            } else if (type === CAYUSE_TYPES.CAYUSE_SUMMARY_BUDGET_IMPORTER) {
                const alreadyAdded = Array.from(acc.values()).find(c => c.field.cayuseType === CAYUSE_TYPES.CAYUSE_SUMMARY_BUDGET_IMPORTER);
                if(!alreadyAdded) {
                    const key = conflict.path;
                    acc.set(key, conflict);
                }
            } else {
                const key = conflict.path;
                acc.set(key, conflict);
            }

            return acc;
        },
        new Map(),
    );

    const groupConflicts = Array.from(groupMap.values());

    return groupConflicts.reduce((acc, conflict) => {
        const type = conflict.field?.cayuseType ?? conflict.field?.type;

        switch (type) {
            case CAYUSE_TYPES.RESEARCH_TEAM:
                acc.push(formatConflictMessage.researchTeam(conflict));
                break;
            case CAYUSE_TYPES.CLASSIFICATION_CODES:
                acc.push(
                    formatConflictMessage.classificationCodes(conflict)
                );
                break;
            case FIELD_TYPES.UPLOAD:
                acc.push(formatConflictMessage.upload(conflict));
                break;
            default:
                const hideValues = [
                    CAYUSE_TYPES.ORG_FINDER,
                    CAYUSE_TYPES.PERSON_FINDER,
                    CAYUSE_TYPES.UNIT_FINDER,
                    FIELD_TYPES.CAYUSE_CHECKBOXES,
                ].includes(type);
                acc.push(
                    formatConflictMessage.base(conflict, hideValues)
                );
                break;
        }

        return acc.filter(Boolean);
    }, []);
};
